import { useMemo } from "react";
import { Megaphone } from "lucide-react";
import {
  SectionCard, SectionHeader, KpiChip, ChartCard, MiniPie, MiniBar, inr, monthKey, last6Months, SECTION_ACCENTS,
} from "./dashboardUi";

const LEAD_STATUS_LABEL = { open: "Open", accepted: "Accepted", on_hold: "On Hold", dropped: "Dropped", converted: "Converted" };
const INSTALL_LABEL_SHORT = {
  not_logged: "Not Logged", scheduled: "Scheduled", in_progress: "In Progress", on_hold: "On Hold",
  support_required: "Support Req.", installation_completed: "Completed", closed: "Closed",
};
const C = SECTION_ACCENTS.sc.chart;

export default function ScSection({ scs, leads, opportunities, payouts, delay }) {
  const insights = useMemo(() => {
    const scStatus = { active: 0, pending_approval: 0, suspended: 0 };
    for (const u of scs) scStatus[u.status || "active"] = (scStatus[u.status || "active"] || 0) + 1;

    const scLeads = leads.filter((l) => l.sc_user_id);
    const leadStatusCounts = {};
    for (const l of scLeads) leadStatusCounts[l.status] = (leadStatusCounts[l.status] || 0) + 1;

    const scOpps = opportunities.filter((o) => o.sc_user_id);
    const installCounts = {};
    for (const o of scOpps) {
      const k = o.installation_status || "not_logged";
      installCounts[k] = (installCounts[k] || 0) + 1;
    }
    const wonScOpps = scOpps.filter((o) => ["won", "won_verbal"].includes(o.status)).length;

    const scPayouts = payouts.filter((p) => p.referrer_type === "sc");
    const payoutByStatus = { pending: 0, processing: 0, paid: 0 };
    for (const p of scPayouts) payoutByStatus[p.payment_status || "pending"] += Number(p.payment_amount || 0);

    const byMonth = {};
    for (const l of scLeads) { const k = monthKey(l.created_at); if (k) byMonth[k] = (byMonth[k] || 0) + 1; }

    const leaderboard = [...scs]
      .sort((a, b) => (b.lead_count || 0) - (a.lead_count || 0))
      .slice(0, 6)
      .map((u) => ({ month: (u.name || "—").length > 12 ? u.name.slice(0, 11) + "…" : u.name, value: u.lead_count || 0 }));

    return {
      totalScs: scs.length, scStatus,
      totalScLeads: scLeads.length,
      leadStatusData: Object.entries(leadStatusCounts).map(([k, value]) => ({ name: LEAD_STATUS_LABEL[k] || k, value })),
      installData: Object.entries(installCounts).map(([k, value]) => ({ name: INSTALL_LABEL_SHORT[k] || k, value })),
      wonScOpps, scOppsTotal: scOpps.length,
      payoutByStatus,
      payoutPieData: Object.entries(payoutByStatus).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0),
      trendData: last6Months(byMonth),
      leaderboard,
    };
  }, [scs, leads, opportunities, payouts]);

  return (
    <SectionCard accent="sc" delay={delay} testid="dashboard-sc-section">
      <SectionHeader icon={Megaphone} accent="sc" title="Solar Consultants" subtitle="Network health, referred leads and payout pipeline" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Total SCs" value={insights.totalScs} tone="bg-[#B45309] text-white" to="/crm/solar-consultants" />
        <KpiChip label="Active" value={insights.scStatus.active || 0} tone="bg-emerald-50 text-emerald-800" to="/crm/solar-consultants" />
        <KpiChip label="Pending Approval" value={insights.scStatus.pending_approval || 0} tone="bg-amber-50 text-amber-800" to="/crm/solar-consultants" />
        <KpiChip label="Leads Referred" value={insights.totalScLeads} to="/crm/leads" />
        <KpiChip label="Opportunities Won" value={insights.wonScOpps} sub={insights.scOppsTotal} to="/crm/opportunities" />
        <KpiChip label="Pending Payout" value={inr(insights.payoutByStatus.pending)} tone="bg-slate-100 text-slate-700" to="/crm/referrer-payouts" />
        <KpiChip label="Paid Payout" value={inr(insights.payoutByStatus.paid)} tone="bg-emerald-50 text-emerald-800" to="/crm/referrer-payouts" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-sc-charts">
        <ChartCard title="SC Lead Status" testid="chart-sc-lead-status"><MiniPie data={insights.leadStatusData} colors={C} to="/crm/leads" /></ChartCard>
        <ChartCard title="SC Opportunity · Install Status" testid="chart-sc-install-status"><MiniPie data={insights.installData} colors={C} to="/crm/opportunities" /></ChartCard>
        <ChartCard title="SC Payout Status (₹)" testid="chart-sc-payout"><MiniPie data={insights.payoutPieData} colors={C} formatter={(v) => inr(v)} to="/crm/referrer-payouts" /></ChartCard>
        <ChartCard title="Top SCs by Leads Referred" testid="chart-sc-leaderboard"><MiniBar data={insights.leaderboard} color={C[0]} to="/crm/solar-consultants" /></ChartCard>
      </div>
    </SectionCard>
  );
}
